/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DevicesModal.tsx
 🗓️ Created: 2025-12-10
 🧩 Description:
    Modal to display device registration info for an extension.
 ────────────────────────────────────────────────────────────────*/

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Phone, Loader2 } from 'lucide-react';
import { fetchDeviceRegistrations, DeviceRegistration, ExtensionStatusEntry } from './api';

interface DevicesModalProps {
    open: boolean;
    onClose: () => void;
    extension: ExtensionStatusEntry;
}

export default function DevicesModal({ open, onClose, extension }: DevicesModalProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [devices, setDevices] = useState<DeviceRegistration[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && extension) {
            setLoading(true);
            setError(null);
            fetchDeviceRegistrations(extension.id)
                .then(data => {
                    setDevices(data);
                })
                .catch(err => {
                    setError(err.message || 'Failed to load device registrations');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open, extension]);

    if (!open) return null;

    const getStatusBadge = (status: DeviceRegistration['status']) => {
        const statusStyles: Record<string, string> = {
            'Registered': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
            'Reachable': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
            'Unregistered': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
            'Unknown': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
        };

        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles['Unknown']}`}>
                {status}
            </span>
        );
    };

    return createPortal(
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-4xl mx-4 bg-card rounded-xl shadow-2xl border border-border overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-muted/50 border-b border-border">
                    <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">
                            {t('extensionStatus.devicesInfo', 'Devices Info')}
                        </h2>
                        <span className="text-sm text-muted-foreground">
                            - {extension.extension} {extension.name && `(${extension.name})`}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-destructive">
                            {error}
                        </div>
                    ) : devices.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            {t('extensionStatus.noDevices', 'No devices registered')}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.usernameChannel', 'Username / Channel')}
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.host', 'Host')}
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.port', 'Port')}
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.status', 'Status')}
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.userAgent', 'User Agent')}
                                        </th>
                                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                                            {t('extensionStatus.description', 'Description')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {devices.map((device, index) => (
                                        <tr
                                            key={index}
                                            className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                                        >
                                            <td className="px-4 py-3 font-mono text-foreground">
                                                {device.channel}
                                            </td>
                                            <td className="px-4 py-3">
                                                <a
                                                    href={device.host}
                                                    className="text-primary hover:underline"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {device.host || '-'}
                                                </a>
                                            </td>
                                            <td className="px-4 py-3 text-foreground">
                                                {device.port}
                                            </td>
                                            <td className="px-4 py-3">
                                                {getStatusBadge(device.status)}
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                                                {device.userAgent || '-'}
                                            </td>
                                            <td className="px-4 py-3 text-foreground">
                                                {device.description || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4 bg-muted/30 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
                    >
                        {t('common.close', 'Close')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
